import type { Aspiration } from '../../../types/aspirations';
import type { Goal } from '../../../types/goals';
import type { List } from '../../../types/lists';

// Single Health & Fitness aspiration based on godmode-health-data.md
export const aspirations: Aspiration[] = [
  {
    id: 'aspiration_optimal_health',
    title: 'Optimal Health & Vitality',
    description: 'Achieve and maintain peak physical fitness through consistent strength training and cardiovascular conditioning',
    category: 'Health',
    priority: 10,
    status: 'in_progress',
    progress: 30,
    deadline: new Date('2025-11-27'),
    createdAt: new Date('2024-09-01'),
    updatedAt: new Date('2024-09-05'),
    linkedGoals: [
      { id: 'goal_cardiovascular_excellence', title: 'Build Elite Cardiovascular Endurance', completed: false, deadline: new Date('2026-12-31') },
      { id: 'goal_functional_strength', title: 'Build Functional Strength & Muscle', completed: false, deadline: new Date('2026-12-31') },
      { id: 'goal_nutrition_recovery', title: 'Optimize Nutrition & Recovery', completed: false, deadline: new Date('2025-12-31') }
    ]
  }
];

export const goals: Goal[] = [
  {
    id: 'goal_cardiovascular_excellence',
    title: 'Build Elite Cardiovascular Endurance',
    description: 'Develop superior aerobic capacity through structured Zone 2 training and interval work',
    aspirationId: 'aspiration_optimal_health',
    priority: 9,
    status: 'in_progress',
    progress: 30,
    
    // SMART Criteria
    smartCriteria: {
      specific: 'Improve VO2 max to above 45 ml/kg/min and maintain resting heart rate below 60 bpm through consistent Zone 2 training and HIIT sessions',
      measurable: 'Track via monthly VO2 max tests, daily resting heart rate measurements, and weekly training volume in Zone 2 (180+ minutes)',
      achievable: 'Currently at VO2 max of 38, with 4-5 hours available weekly for cardio training. Have access to gym and outdoor running routes',
      relevant: 'Cardiovascular fitness is the foundation of longevity and daily energy levels, directly supporting my health aspiration',
      timeBound: 'Achieve target metrics by December 31, 2024, with monthly progress checkpoints'
    },
    
    startDate: new Date('2024-09-01'),
    deadline: new Date('2024-12-31'),
    createdAt: new Date('2024-09-01'),
    updatedAt: new Date('2024-09-05'),
    
    successMetrics: [
      'VO2 max above 45 ml/kg/min',
      'Resting heart rate consistently below 60 bpm',
      'Complete 180+ minutes Zone 2 weekly',
      '5K time under 25 minutes'
    ],
    resources: [
      'Heart rate monitor',
      'Treadmill/bike access',
      'Running shoes',
      'Zone 2 training plan'
    ],
    lists: [
      {
        id: 'list_zone_2',
        title: 'Zone 2',
        description: 'Weekly cardio workouts to improve VO2 max and anaerobic capacity',
        deadline: new Date('2026-10-10'),
        completed: false
      },
      {
        id: 'list_zone_3',
        title: 'Zone 3',
        description: 'Structured Zone 3 training to improve VO2 max and anaerobic capacity',
        deadline: new Date('2026-10-03'),
        completed: false
      },
      {
        id: 'list_hiit_sessions',
        title: 'HIIT Sessions',
        description: 'High-intensity interval training for VO2 max improvement',
        deadline: new Date('2026-10-15'),
        completed: false
      }
    ]
  },
  {
    id: 'goal_functional_strength',
    title: 'Build Functional Strength & Muscle',
    description: 'Develop practical strength through progressive overload focusing on compound movements',
    aspirationId: 'aspiration_optimal_health',
    priority: 9,
    status: 'in_progress',
    progress: 45,
    
    // SMART Criteria
    smartCriteria: {
      specific: 'Achieve 2x bodyweight deadlift, 1.5x bodyweight squat, 1x bodyweight bench press, and 10+ strict pull-ups',
      measurable: 'Track via weekly strength tests, monthly body composition scans, and workout log progression',
      achievable: 'Currently deadlifting 1.5x bodyweight with consistent 3-4x weekly training schedule established',
      relevant: 'Strength training preserves muscle mass, bone density, and metabolic health as I age',
      timeBound: 'Reach all strength targets by March 31, 2025'
    },
    
    startDate: new Date('2024-09-01'),
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-09-01'),
    updatedAt: new Date('2024-09-05'),
    
    successMetrics: [
      'Deadlift 2x bodyweight',
      'Squat 1.5x bodyweight',
      'Bench press 1x bodyweight',
      '10+ consecutive pull-ups',
      'Maintain lean muscle mass above 155 lbs'
    ],
    resources: [
      'Gym membership',
      'Progressive overload program',
      'Lifting belt and straps',
      'Protein supplementation',
      'Form coaching'
    ],
    lists: [
      {
        id: 'list_strength',
        title: 'Full body',
        description: 'Progressive overload program focusing on all body parts',
        deadline: new Date('2026-10-03'),
        completed: false
      },
      {
        id: 'list_upper_body',
        title: 'Upper Body Focus',
        description: 'Upper body strength training sessions',
        deadline: new Date('2026-10-03'),
        completed: false
      },
      {
        id: 'list_lower_body',
        title: 'Lower Body Focus',
        description: 'Lower body strength training sessions',
        deadline: new Date('2026-10-05'),
        completed: false
      }
    ]
  },
  {
    id: 'goal_nutrition_recovery',
    title: 'Optimize Nutrition & Recovery',
    description: 'Establish optimal nutrition habits and recovery protocols to support training and overall health',
    aspirationId: 'aspiration_optimal_health',
    priority: 8,
    status: 'in_progress',
    progress: 35,

    smartCriteria: {
      specific: 'Consume 180g protein daily, sleep 7-8 hours nightly, and maintain proper hydration (3L water)',
      measurable: 'Track via nutrition app, sleep tracker, and daily water intake log',
      achievable: 'Meal prep on weekends, establish consistent sleep schedule, carry water bottle',
      relevant: 'Proper nutrition and recovery are essential for achieving strength and endurance goals',
      timeBound: 'Establish consistent habits by December 31, 2025'
    },

    startDate: new Date('2024-09-01'),
    deadline: new Date('2025-12-31'),
    createdAt: new Date('2024-09-01'),
    updatedAt: new Date('2024-09-05'),

    successMetrics: [
      'Daily protein intake 180g+',
      'Sleep 7-8 hours consistently',
      'Water intake 3L daily',
      'Weekly meal prep completed'
    ],
    resources: [
      'Nutrition tracking app',
      'Sleep tracker',
      'Meal prep containers',
      'Protein supplements',
      'Water bottle'
    ],
    lists: [
      {
        id: 'list_meal_prep',
        title: 'Weekly Meal Prep',
        description: 'Sunday meal preparation for the week',
        deadline: new Date('2025-12-31'),
        completed: false
      },
      {
        id: 'list_sleep_routine',
        title: 'Sleep Optimization',
        description: 'Evening routine for quality sleep',
        deadline: new Date('2025-12-31'),
        completed: false
      },
      {
        id: 'list_supplements',
        title: 'Daily Supplements',
        description: 'Supplement protocol for optimal recovery',
        deadline: new Date('2025-12-31'),
        completed: false
      }
    ]
  }
];

export const lists: List[] = [
  {
    id: 'list_zone_3',
    title: 'Zone 3',
    description: 'structured Zone 3 training to improve VO2 max and anaerobic capacity',
    goalId: 'goal_cardiovascular_excellence',
    priority: 10,
    status: 'in_progress',
    startDate: new Date('2024-09-05'),
    deadline: new Date('2026-10-03'),
    color: '#FF6B6B',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_zone_3_warmup_1',
        content: 'Warmup: treadmill 50 calories',
        description: 'burn 50 calories',
        priority: 1,
        dueDate: new Date('2024-09-09'),
        estimatedMinutes: 5,
        category: 'Cardio',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_zone_3_warmup_2',
        content: 'Warmup: bike 50 calories',
        description: 'burn 50 calories',
        priority: 2,
        dueDate: new Date('2024-09-11'),
        estimatedMinutes: 5,
        category: 'Cardio',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_zone_3_main_1',
        content: 'Row: 2.5k in zone 3',
        description: 'row 2.5k in zone 3',
        priority: 3,
        dueDate: new Date('2024-09-13'),
        estimatedMinutes: 25,
        category: 'Cardio',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
    ]
  },
  {
    id: 'list_strength',
    title: 'Full body',
    description: 'Progressive overload program focusing on all body parts',
    goalId: 'goal_functional_strength',
    priority: 10,
    status: 'in_progress',
    startDate: new Date('2024-09-05'),
    deadline: new Date('2026-10-03'),
    color: '#4ECDC4',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_bench_press',
        content: 'DB Incline Bench Press 3 x 8 34kg',
        description: 'push movement focusing on progressive overload',
        priority: 3,
        dueDate: new Date('2024-09-06'),
        estimatedMinutes: 20,
        category: 'Strength',
        difficulty: 'hard',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_incline_sit_up',
        content: 'Incline Sit-Up 3 x 8 0kg',
        description: 'push movement focusing on progressive overload',
        priority: 2,
        dueDate: new Date('2024-09-06'),
        estimatedMinutes: 10,
        category: 'Strength',
        difficulty: 'hard',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_pullups',
        content: 'Pull-ups 3 x 8 14kg',
        description: 'pull movement focusing on progressive overload',
        priority: 1,
        dueDate: new Date('2024-09-06'),
        estimatedMinutes: 10,
        category: 'Strength',
        difficulty: 'hard',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_overhead_press',
        content: 'Overhead DB Press 3 x 8 20kg',
        description: 'push movement focusing on progressive overload',
        priority: 4,
        dueDate: new Date('2024-09-06'),
        estimatedMinutes: 10,
        category: 'Strength',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_lower_single_leg_bench_lunge',
        content: 'Single Leg Bench Lunge 3 x 10 0kg',
        description: 'lunge movement focusing on progressive overload',
        priority: 5,
        dueDate: new Date('2024-09-08'),
        estimatedMinutes: 10,
        category: 'Strength',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  {
    id: 'list_zone_2',
    title: 'Zone 2',
    description: 'Weekly cardio workouts to improve VO2 max and anaerobic capacity',
    goalId: 'goal_cardiovascular_excellence',
    priority: 8,
    status: 'ready',
    startDate: new Date('2024-09-10'),
    deadline: new Date('2026-10-10'),
    color: '#45B7D1',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_warmup_1',
        content: 'Treadmill Warmup: 50 calories',
        description: 'burn 50 calories',
        priority: 1,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 10,
        category: 'Cardio',
        difficulty: 'easy',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_warmup_2',
        content: 'Bike Warmup: 50 calories',
        description: 'burn 50 calories',
        priority: 1,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 10,
        category: 'Cardio',
        difficulty: 'easy',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },

      {
        id: 'item_rowing_zone_2',
        content: '5k row',
        description: 'row 5k',
        priority: 4,
        dueDate: new Date('2024-09-24'),
        estimatedMinutes: 25,
        category: 'Cardio',
        difficulty: 'medium',
        notes: '',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // Third list for cardiovascular goal
  {
    id: 'list_hiit_sessions',
    title: 'HIIT Sessions',
    description: 'High-intensity interval training for VO2 max improvement',
    goalId: 'goal_cardiovascular_excellence',
    priority: 9,
    status: 'ready',
    startDate: new Date('2024-09-15'),
    deadline: new Date('2026-10-15'),
    color: '#FF9F43',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_hiit_1',
        content: 'Tabata sprints: 8x20s on/10s off',
        description: 'All-out effort sprints on bike or treadmill',
        priority: 1,
        dueDate: new Date('2024-09-16'),
        estimatedMinutes: 15,
        category: 'Cardio',
        difficulty: 'hard',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_hiit_2',
        content: 'Assault bike intervals: 5x1min on/1min off',
        description: 'Sustained high-intensity efforts',
        priority: 2,
        dueDate: new Date('2024-09-18'),
        estimatedMinutes: 20,
        category: 'Cardio',
        difficulty: 'hard',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_hiit_3',
        content: 'Rowing intervals: 500m repeats x 6',
        description: 'Target 1:45/500m pace with 90s rest',
        priority: 3,
        dueDate: new Date('2024-09-20'),
        estimatedMinutes: 25,
        category: 'Cardio',
        difficulty: 'hard',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // Second list for strength goal
  {
    id: 'list_upper_body',
    title: 'Upper Body Focus',
    description: 'Upper body strength training sessions',
    goalId: 'goal_functional_strength',
    priority: 9,
    status: 'in_progress',
    startDate: new Date('2024-09-05'),
    deadline: new Date('2026-10-03'),
    color: '#A29BFE',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_upper_1',
        content: 'Barbell Bench Press 4x6 @80kg',
        description: 'Main pressing movement',
        priority: 1,
        dueDate: new Date('2024-09-07'),
        estimatedMinutes: 20,
        category: 'Strength',
        difficulty: 'hard',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_upper_2',
        content: 'Barbell Rows 4x8 @70kg',
        description: 'Main pulling movement',
        priority: 2,
        dueDate: new Date('2024-09-07'),
        estimatedMinutes: 15,
        category: 'Strength',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_upper_3',
        content: 'Dumbbell Shoulder Press 3x10 @22kg',
        description: 'Accessory pressing',
        priority: 3,
        dueDate: new Date('2024-09-07'),
        estimatedMinutes: 12,
        category: 'Strength',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_upper_4',
        content: 'Face Pulls 3x15',
        description: 'Rear delt and rotator cuff health',
        priority: 4,
        dueDate: new Date('2024-09-07'),
        estimatedMinutes: 8,
        category: 'Strength',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // Third list for strength goal
  {
    id: 'list_lower_body',
    title: 'Lower Body Focus',
    description: 'Lower body strength training sessions',
    goalId: 'goal_functional_strength',
    priority: 9,
    status: 'ready',
    startDate: new Date('2024-09-08'),
    deadline: new Date('2026-10-05'),
    color: '#00CEC9',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_lower_1',
        content: 'Back Squat 4x5 @100kg',
        description: 'Main squat movement',
        priority: 1,
        dueDate: new Date('2024-09-09'),
        estimatedMinutes: 25,
        category: 'Strength',
        difficulty: 'hard',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_lower_2',
        content: 'Romanian Deadlift 3x8 @80kg',
        description: 'Hamstring and posterior chain focus',
        priority: 2,
        dueDate: new Date('2024-09-09'),
        estimatedMinutes: 15,
        category: 'Strength',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_lower_3',
        content: 'Leg Press 3x12 @150kg',
        description: 'Quad volume',
        priority: 3,
        dueDate: new Date('2024-09-09'),
        estimatedMinutes: 12,
        category: 'Strength',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_lower_4',
        content: 'Calf Raises 4x15',
        description: 'Calf development',
        priority: 4,
        dueDate: new Date('2024-09-09'),
        estimatedMinutes: 8,
        category: 'Strength',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // First list for nutrition goal
  {
    id: 'list_meal_prep',
    title: 'Weekly Meal Prep',
    description: 'Sunday meal preparation for the week',
    goalId: 'goal_nutrition_recovery',
    priority: 8,
    status: 'in_progress',
    startDate: new Date('2024-09-08'),
    deadline: new Date('2025-12-31'),
    color: '#FD79A8',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_meal_1',
        content: 'Prep 5 chicken breasts (200g each)',
        description: 'Season and bake for lunches',
        priority: 1,
        dueDate: new Date('2024-09-08'),
        estimatedMinutes: 45,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_meal_2',
        content: 'Cook 1kg brown rice',
        description: 'Carb source for the week',
        priority: 2,
        dueDate: new Date('2024-09-08'),
        estimatedMinutes: 30,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_meal_3',
        content: 'Prep vegetables: broccoli, spinach, peppers',
        description: 'Wash, chop and store',
        priority: 3,
        dueDate: new Date('2024-09-08'),
        estimatedMinutes: 20,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_meal_4',
        content: 'Hard boil 12 eggs',
        description: 'Quick protein snacks',
        priority: 4,
        dueDate: new Date('2024-09-08'),
        estimatedMinutes: 15,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // Second list for nutrition goal
  {
    id: 'list_sleep_routine',
    title: 'Sleep Optimization',
    description: 'Evening routine for quality sleep',
    goalId: 'goal_nutrition_recovery',
    priority: 7,
    status: 'in_progress',
    startDate: new Date('2024-09-10'),
    deadline: new Date('2025-12-31'),
    color: '#6C5CE7',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_sleep_1',
        content: 'No screens after 9pm',
        description: 'Reduce blue light exposure',
        priority: 1,
        dueDate: new Date('2024-09-10'),
        estimatedMinutes: 0,
        category: 'Recovery',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_sleep_2',
        content: 'Bedroom temp to 18°C',
        description: 'Optimal sleep temperature',
        priority: 2,
        dueDate: new Date('2024-09-10'),
        estimatedMinutes: 2,
        category: 'Recovery',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_sleep_3',
        content: '10 minutes stretching/foam rolling',
        description: 'Release muscle tension',
        priority: 3,
        dueDate: new Date('2024-09-10'),
        estimatedMinutes: 10,
        category: 'Recovery',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_sleep_4',
        content: 'Lights out by 10pm',
        description: 'Target 8 hours sleep',
        priority: 4,
        dueDate: new Date('2024-09-10'),
        estimatedMinutes: 0,
        category: 'Recovery',
        difficulty: 'medium',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  },
  // Third list for nutrition goal
  {
    id: 'list_supplements',
    title: 'Daily Supplements',
    description: 'Supplement protocol for optimal recovery',
    goalId: 'goal_nutrition_recovery',
    priority: 6,
    status: 'ready',
    startDate: new Date('2024-09-12'),
    deadline: new Date('2025-12-31'),
    color: '#FDCB6E',
    createdAt: new Date('2024-09-05'),
    updatedAt: new Date('2024-09-05'),
    items: [
      {
        id: 'item_supp_1',
        content: 'Creatine 5g with breakfast',
        description: 'Strength and recovery support',
        priority: 1,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 1,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_supp_2',
        content: 'Fish oil 2g with lunch',
        description: 'Omega-3 for inflammation',
        priority: 2,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 1,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_supp_3',
        content: 'Vitamin D 4000IU morning',
        description: 'Immune and bone health',
        priority: 3,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 1,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      },
      {
        id: 'item_supp_4',
        content: 'Magnesium 400mg before bed',
        description: 'Sleep and recovery',
        priority: 4,
        dueDate: new Date('2024-09-12'),
        estimatedMinutes: 1,
        category: 'Nutrition',
        difficulty: 'easy',
        createdAt: new Date('2024-09-05'),
        updatedAt: new Date('2024-09-05')
      }
    ]
  }
];